import { useReducer, useMemo } from 'react';
import {
  GridState,
  GridColumn,
  GridView,
  GridPagination,
  SortState,
  SortDirection,
} from '../../../../types/datagrid';

// Action types
type GridAction =
  | {
      type: 'UPDATE_DATA';
      payload: { data: any[]; loading: boolean; error?: string; pagination?: GridPagination };
    }
  | { type: 'UPDATE_COLUMNS'; payload: GridColumn[] }
  | { type: 'UPDATE_SORTING'; payload: SortState[] }
  | { type: 'UPDATE_FILTERS'; payload: Record<string, any> }
  | { type: 'UPDATE_PAGINATION'; payload: GridPagination }
  | { type: 'UPDATE_VIEW'; payload: GridView }
  | { type: 'START_EDIT'; payload: { rowId: string; columnId: string; value: any } }
  | { type: 'EDIT_CELL_SUCCESS' }
  | { type: 'EDIT_CELL_ERROR'; payload: string }
  | { type: 'CANCEL_EDIT' }
  | { type: 'SELECT_ROWS'; payload: string[] }
  | { type: 'RESET_STATE' };

// Initial state factory
const createInitialState = (
  data: any[],
  columns: GridColumn[],
  defaultView?: GridView,
  loading = false,
  error?: string,
  pagination?: GridPagination
): GridState => ({
  data: data || [],
  columns: columns || [],
  sorting: defaultView?.sorting || [],
  filters: defaultView?.filters || {},
  pagination: pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
  loading,
  error,
  selectedRows: [],
  editingCell: undefined,
  viewType: 'list',
  currentView: defaultView,
  availableViews: defaultView ? [defaultView] : [],
});

// Reducer function
const gridReducer = (state: GridState, action: GridAction): GridState => {
  switch (action.type) {
    case 'UPDATE_DATA':
      return {
        ...state,
        data: action.payload.data,
        loading: action.payload.loading,
        error: action.payload.error,
        pagination: action.payload.pagination || state.pagination,
      };

    case 'UPDATE_COLUMNS':
      return {
        ...state,
        columns: action.payload,
      };

    case 'UPDATE_SORTING':
      return {
        ...state,
        sorting: action.payload,
      };

    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: action.payload,
      };

    case 'UPDATE_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };

    case 'UPDATE_VIEW':
      return {
        ...state,
        currentView: action.payload,
        columns: action.payload.columns,
        sorting: action.payload.sorting,
        filters: action.payload.filters,
        pagination: {
          ...state.pagination,
          ...action.payload.pagination,
        },
      };

    case 'START_EDIT':
      return {
        ...state,
        editingCell: action.payload,
        error: undefined,
      };

    case 'EDIT_CELL_SUCCESS':
      return {
        ...state,
        editingCell: undefined,
        error: undefined,
      };

    case 'EDIT_CELL_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'CANCEL_EDIT':
      return {
        ...state,
        editingCell: undefined,
        error: undefined,
      };

    case 'SELECT_ROWS':
      return {
        ...state,
        selectedRows: action.payload,
      };

    case 'RESET_STATE':
      return createInitialState(
        state.data,
        state.columns,
        state.currentView,
        state.loading,
        undefined,
        state.pagination
      );

    default:
      return state;
  }
};

interface UseDataGridParams {
  gridId: string;
  data: any[];
  columns: GridColumn[];
  defaultView?: GridView;
  loading?: boolean;
  error?: string;
  pagination?: GridPagination;
}

export const useDataGrid = ({
  data,
  columns,
  defaultView,
  loading = false,
  error,
  pagination,
}: UseDataGridParams) => {
  // Initialize state with reducer
  const [state, dispatch] = useReducer(
    gridReducer,
    createInitialState(data, columns, defaultView, loading, error, pagination)
  );

  // Memoized selectors
  const selectors = useMemo(
    () => ({
      visibleColumns: state.columns.filter(col => col.isVisible),
      sortableColumns: state.columns.filter(col => col.isSortable),
      editableColumns: state.columns.filter(col => col.isEditable),
      pinnedLeftColumns: state.columns.filter(col => col.isPinned === 'left'),
      pinnedRightColumns: state.columns.filter(col => col.isPinned === 'right'),
      unpinnedColumns: state.columns.filter(col => !col.isPinned),

      hasActiveFilters: Object.values(state.filters).some(
        value =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0)
      ),

      hasActiveSorting: state.sorting.length > 0,

      isLoading: state.loading,

      hasError: !!state.error,

      hasSelectedRows: state.selectedRows.length > 0,

      isEditing: !!state.editingCell,

      totalResults: state.data.length,
    }),
    [state]
  );

  // Action creators
  const actions = useMemo(
    () => ({
      updateData: (
        newData: any[],
        loading = false,
        error?: string,
        pagination?: GridPagination
      ) => {
        dispatch({
          type: 'UPDATE_DATA',
          payload: { data: newData, loading, error, pagination },
        });
      },

      updateColumns: (columns: GridColumn[]) => {
        dispatch({ type: 'UPDATE_COLUMNS', payload: columns });
      },

      updateSorting: (sorting: SortState[]) => {
        dispatch({ type: 'UPDATE_SORTING', payload: sorting });
      },

      updateFilters: (filters: Record<string, any>) => {
        dispatch({ type: 'UPDATE_FILTERS', payload: filters });
      },

      updatePagination: (pagination: GridPagination) => {
        dispatch({ type: 'UPDATE_PAGINATION', payload: pagination });
      },

      updateView: (view: GridView) => {
        dispatch({ type: 'UPDATE_VIEW', payload: view });
      },

      startEdit: (rowId: string, columnId: string, value: any) => {
        dispatch({ type: 'START_EDIT', payload: { rowId, columnId, value } });
      },

      finishEdit: () => {
        dispatch({ type: 'EDIT_CELL_SUCCESS' });
      },

      errorEdit: (error: string) => {
        dispatch({ type: 'EDIT_CELL_ERROR', payload: error });
      },

      cancelEdit: () => {
        dispatch({ type: 'CANCEL_EDIT' });
      },

      selectRows: (rowIds: string[]) => {
        dispatch({ type: 'SELECT_ROWS', payload: rowIds });
      },

      clearSelection: () => {
        dispatch({ type: 'SELECT_ROWS', payload: [] });
      },

      resetState: () => {
        dispatch({ type: 'RESET_STATE' });
      },
    }),
    []
  );

  // Utility functions
  const utils = useMemo(
    () => ({
      getColumnById: (columnId: string) => state.columns.find(col => col.id === columnId),

      getColumnByKey: (key: string) => state.columns.find(col => col.key === key),

      isColumnVisible: (columnId: string) =>
        state.columns.find(col => col.id === columnId)?.isVisible ?? false,

      isColumnSortable: (columnId: string) =>
        state.columns.find(col => col.id === columnId)?.isSortable ?? false,

      isColumnEditable: (columnId: string) =>
        state.columns.find(col => col.id === columnId)?.isEditable ?? false,

      getSortDirection: (columnId: string): SortDirection => {
        const sort = state.sorting.find(s => s.field === columnId);
        return sort?.direction || null;
      },

      getSortOrder: (columnId: string): number => {
        const sortIndex = state.sorting.findIndex(s => s.field === columnId);
        return sortIndex >= 0 ? sortIndex + 1 : 0;
      },

      isRowSelected: (rowId: string): boolean => state.selectedRows.includes(rowId),

      isEditingCell: (rowId: string, columnId: string): boolean =>
        state.editingCell?.rowId === rowId && state.editingCell?.columnId === columnId,

      getFilterValue: (filterId: string) => state.filters[filterId],

      hasFilter: (filterId: string): boolean => {
        const value = state.filters[filterId];
        return (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0)
        );
      },
    }),
    [state]
  );

  return {
    state,
    dispatch,
    selectors,
    actions,
    utils,
  };
};
